import type {
  MeetingInsights,
  StructuredTask,
  SummaryStructured,
  VideoScreenInsights,
} from '../../../services/api.service';

export type ChatSection =
  | 'protocol'
  | 'summary'
  | 'tasks'
  | 'transcription'
  | 'analytics'
  | 'video_insights'
  | 'general';

export type ChatContext = {
  section?: ChatSection;
  meetingName?: string;
  hasProtocol?: boolean;
  hasTranscription?: boolean;
  insights?: MeetingInsights | null;
  summaryStructured?: SummaryStructured | null;
  videoScreenInsights?: VideoScreenInsights | null;
  structuredTasks?: StructuredTask[];
  speakers?: string[];
};

function truncate(text: string, maxLen = 72): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1).trim()}…`;
}

function addUnique(items: string[], seen: Set<string>, question: string): void {
  const q = question.trim();
  if (!q) return;
  const key = q.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  items.push(q);
}

function meetingPhrase(meetingName?: string): string {
  const name = (meetingName || '').trim();
  if (!name) return 'текущего совещания';

  const slug = name.match(/^([a-zA-Zа-яА-ЯёЁ0-9]+)[_\-](\d{2})(\d{2})(\d{2,4})$/i);
  if (slug) {
    const [, prefix, day, month, year] = slug;
    const yr = year.length === 4 ? year : `20${year}`;
    const label = /^[a-z]+$/i.test(prefix) ? prefix.toUpperCase() : prefix;
    return `совещания ${label} от ${day}.${month}.${yr}`;
  }
  if (/[_\-]/.test(name) && /\d/.test(name)) return 'текущего совещания';
  return `совещания «${truncate(name, 48)}»`;
}


function collectActionItems(
  insights?: MeetingInsights | null,
  structuredTasks?: StructuredTask[],
): { text: string; assignee: string }[] {
  const items: { text: string; assignee: string }[] = [];
  insights?.action_items?.forEach(item => {
    const text = (item.text || '').trim();
    if (text) items.push({ text, assignee: (item.assignee || '').trim() });
  });
  structuredTasks?.forEach(task => {
    const text = (task.task || '').trim();
    if (text) items.push({ text, assignee: (task.assignee || '').trim() });
  });
  return items;
}

function leaderName(speakers: string[]): string | undefined {
  const named = speakers.filter(s => s?.trim() && !/^SPEAKER_/i.test(s));
  return named.find(s => /галихан/i.test(s)) || named[0];
}

function buildProtocolSuggestions(
  items: string[],
  seen: Set<string>,
  ctx: ChatContext,
  actionItems: { text: string; assignee: string }[],
  phrase: string,
): void {
  addUnique(items, seen, 'Какие решения и поручения зафиксированы в протоколе?');
  addUnique(items, seen, 'Есть ли открытые вопросы, требующие эскалации руководству?');

  ctx.summaryStructured?.decisions?.slice(0, 2).forEach(decision => {
    const d = truncate(String(decision), 52);
    if (d) addUnique(items, seen, `Какое решение принято: «${d}»?`);
  });

  actionItems.slice(0, 2).forEach(item => {
    const text = truncate(item.text, 48);
    if (!text) return;
    if (item.assignee) {
      addUnique(items, seen, `Какое поручение у ${item.assignee}: «${text}»?`);
    } else {
      addUnique(items, seen, `Что зафиксировано в протоколе: «${text}»?`);
    }
  });

  addUnique(items, seen, `Кратко: итоги ${phrase} для протокола.`);
}

function buildSummarySuggestions(
  items: string[],
  seen: Set<string>,
  ctx: ChatContext,
): void {
  addUnique(items, seen, 'Кратко: основные темы текущего совещания.');
  addUnique(items, seen, 'Какие ключевые выводы важны для руководства?');

  ctx.summaryStructured?.key_topics?.slice(0, 2).forEach(topic => {
    const t = truncate(String(topic), 52);
    if (t) addUnique(items, seen, `Что важного обсуждали по теме «${t}»?`);
  });

  ctx.insights?.key_topics?.slice(0, 2).forEach(topic => {
    const t = truncate(topic.topic || '', 52);
    if (t) addUnique(items, seen, `Резюме обсуждения: «${t}».`);
  });

  ctx.videoScreenInsights?.highlights?.slice(0, 1).forEach(hl => {
    const h = truncate(hl, 56);
    if (h) addUnique(items, seen, `Что означает для бизнеса: ${h}`);
  });

  const tone = ctx.insights?.tone_summary;
  if (tone && (tone.overall === 'tense' || (tone.tension_count ?? 0) > 0)) {
    const label = tone.label?.trim() || 'напряжённый';
    addUnique(items, seen, `Какие риски и спорные моменты отмечены (${label} тон)?`);
  }
}

function buildTasksSuggestions(
  items: string[],
  seen: Set<string>,
  actionItems: { text: string; assignee: string }[],
  structuredTasks?: StructuredTask[],
): void {
  addUnique(items, seen, 'Сводка: кто что должен сделать после совещания?');
  addUnique(items, seen, 'Какие поручения срочные и к какому сроку?');

  const urgent = actionItems.find(item => /16:00|сроч|сегодня|завтра/i.test(item.text));
  if (urgent) {
    const q = truncate(urgent.text, 52);
    if (urgent.assignee) {
      addUnique(items, seen, `Срочное поручение для ${urgent.assignee}: «${q}»?`);
    } else {
      addUnique(items, seen, `Срочное поручение: «${q}» — кто отвечает?`);
    }
  }

  structuredTasks?.slice(0, 2).forEach(task => {
    const text = truncate(task.task || '', 48);
    const assignee = (task.assignee || '').trim();
    const deadline = (task.deadline || '').trim();
    if (!text) return;
    if (assignee && deadline) {
      addUnique(items, seen, `Задача ${assignee} до ${deadline}: «${text}»?`);
    } else if (assignee) {
      addUnique(items, seen, `Что должен сделать ${assignee}: «${text}»?`);
    }
  });

  actionItems.slice(0, 3).forEach(item => {
    const text = truncate(item.text, 48);
    if (!text) return;
    if (/16:00|сроч/i.test(text)) {
      addUnique(items, seen, `Срочное поручение: «${text}» — кто отвечает?`);
    } else if (item.assignee) {
      addUnique(items, seen, `Статус задачи для ${item.assignee}: «${text}»?`);
    }
  });

  addUnique(items, seen, 'Есть ли задачи без исполнителя или с неясным сроком?');
}

function buildTranscriptionSuggestions(
  items: string[],
  seen: Set<string>,
  ctx: ChatContext,
  leader?: string,
): void {
  if (leader) {
    addUnique(items, seen, `Какие ключевые тезисы озвучил ${leader}?`);
  }

  ctx.insights?.keywords?.slice(0, 2).forEach(kw => {
    const word = (kw.word || '').trim();
    if (word.length >= 4) {
      addUnique(items, seen, `Что говорили про «${word}» — цитаты и смысл.`);
    }
  });

  const dispute = ctx.insights?.key_moments?.find(m =>
    ['conflict', 'dispute', 'спор', 'конфликт'].includes(String(m.tag_type || '').toLowerCase()),
  );
  if (dispute?.text) {
    addUnique(items, seen, `Что вызвало спор: «${truncate(dispute.text, 52)}»?`);
  }

  addUnique(items, seen, 'Где в записи обсуждались цифры, план и факт?');
  addUnique(items, seen, 'Кто из участников говорил больше всего и на какие темы?');
}

function buildAnalyticsSuggestions(items: string[], seen: Set<string>, ctx: ChatContext): void {
  addUnique(items, seen, 'Как распределилось время выступлений между участниками?');
  addUnique(items, seen, 'Насколько продуктивно прошло совещание по метрикам речи?');

  const top = ctx.insights?.engagement_by_speaker?.[0];
  if (top?.speaker) {
    addUnique(items, seen, `Какова вовлечённость ${top.speaker} по аналитике?`);
  }

  const tense = ctx.insights?.speaker_tones?.find(
    st => st.tone === 'tense' && st.speaker && !/^SPEAKER_/i.test(st.speaker),
  );
  if (tense?.speaker) {
    addUnique(items, seen, `Почему тон ${tense.speaker} был напряжённым — что он говорил?`);
  }

  if ((ctx.insights?.tone_summary?.tension_count ?? 0) > 0) {
    addUnique(items, seen, 'Какие эпизоды дали напряжённость в дискуссии?');
  }
}

function buildVideoInsightsSuggestions(
  items: string[],
  seen: Set<string>,
  vi?: VideoScreenInsights | null,
): void {
  addUnique(items, seen, 'Сводка KPI с экрана для доклада руководству.');
  addUnique(items, seen, 'Какие филиалы или направления отстают от плана?');

  vi?.sections?.slice(0, 2).forEach(section => {
    const title = truncate(section.title || '', 52);
    if (title) addUnique(items, seen, `Что показали по разделу «${title}»?`);
  });

  vi?.highlights?.slice(0, 2).forEach(hl => {
    const h = truncate(hl, 58);
    if (h) addUnique(items, seen, `Как интерпретировать показатель: ${h}`);
  });

  const term = vi?.glossary?.[0]?.term?.trim();
  if (term) {
    addUnique(items, seen, `Что означает «${term}» в контексте этой встречи?`);
  }
}

function buildGeneralSuggestions(
  items: string[],
  seen: Set<string>,
  ctx: ChatContext,
  actionItems: { text: string; assignee: string }[],
  phrase: string,
): void {
  addUnique(items, seen, 'Кратко: основные темы текущего совещания.');
  if (ctx.hasProtocol) {
    addUnique(items, seen, 'Какие решения и поручения зафиксированы?');
  }
  const first = actionItems[0];
  if (first?.text) {
    addUnique(items, seen, `Актуальное поручение: «${truncate(first.text, 48)}»?`);
  }
  if (ctx.hasTranscription) {
    addUnique(items, seen, `Главное по итогам ${phrase}.`);
  }
}

/** Подсказки по контексту вкладки и материалам записи (для руководства и бизнеса). */
export function buildChatSuggestions(ctx: ChatContext, limit = 5): string[] {
  const items: string[] = [];
  const seen = new Set<string>();
  const section = ctx.section || 'general';

  if (!ctx.hasProtocol && !ctx.hasTranscription) {
    addUnique(items, seen, 'Что можно узнать после обработки этой записи?');
    return items.slice(0, limit);
  }

  const phrase = meetingPhrase(ctx.meetingName);
  const speakers = (ctx.speakers || []).filter(s => s?.trim());
  const actionItems = collectActionItems(ctx.insights, ctx.structuredTasks);

  switch (section) {
    case 'protocol':
      buildProtocolSuggestions(items, seen, ctx, actionItems, phrase);
      break;
    case 'summary':
      buildSummarySuggestions(items, seen, ctx);
      break;
    case 'tasks':
      buildTasksSuggestions(items, seen, actionItems, ctx.structuredTasks);
      break;
    case 'transcription':
      buildTranscriptionSuggestions(items, seen, ctx, leaderName(speakers));
      break;
    case 'analytics':
      buildAnalyticsSuggestions(items, seen, ctx);
      break;
    case 'video_insights':
      buildVideoInsightsSuggestions(items, seen, ctx.videoScreenInsights);
      break;
    default:
      buildGeneralSuggestions(items, seen, ctx, actionItems, phrase);
  }

  if (items.length < 2) {
    buildGeneralSuggestions(items, seen, ctx, actionItems, phrase);
  }

  return items.slice(0, limit);
}
